import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  Play,
  Home,
  Layers,
  BookOpen,
  Users,
  Mail,
  X,
  LogIn,
} from "lucide-react";
import NotationButtonLabel from "../../../components/ui/NotationButtonLabel";

interface NavItem {
  name: string;
  path: string;
  isHash: boolean;
  hasDropdown?: boolean;
}

interface NavLinkProps {
  item: NavItem;
  isActive: boolean;
  isDropdownOpen: boolean;
  anyDropdownOpen: boolean;
  onToggleDropdown: () => void;
  onCloseDropdown: () => void;
}

const renderNavLabel = (name: string) => {
  if (name === "Notation") {
    return (
      <span
        className="text-[1.05em] leading-none"
        style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}
      >
        notation
      </span>
    );
  }

  if (name === "About Us") {
    return "About us";
  }

  if (name === "Contact Us") {
    return "Contact us";
  }

  return name;
};

const isNavItemActive = (item: NavItem, pathname: string) => {
  if (item.name === "Home") return pathname === "/";
  if (item.name === "Contact Us") return pathname === "/contact";
  if (item.name === "Solutions") return ["/notation", "/demo"].includes(pathname);
  if (item.name === "About Us") {
    return ["/about", "/founder-notes", "/careers"].includes(pathname);
  }
  if (item.name === "Resources") {
    return ["/roi-calculator", "/faq", "/blogs"].includes(pathname);
  }
  return pathname === item.path;
};

const ActiveIndicator = () => (
  <motion.div
    layoutId="navbar-active-indicator"
    className="absolute bottom-0.5 left-2 right-2 h-[2px] rounded-full bg-[#57AAD9] shadow-[0_0_10px_rgba(87,170,217,0.65)]"
    transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
  />
);

// Alias kept for HMR/cache safety during hot reloads
const ActivePill = ActiveIndicator;

type SheetId = "solutions" | "resources" | "about";

interface MobileTab {
  id: string;
  label: string;
  icon: typeof Home;
  to?: string;
  sheet?: SheetId;
  matchPaths?: string[];
}

const MOBILE_TABS: MobileTab[] = [
  { id: "home", label: "Home", icon: Home, to: "/", matchPaths: ["/"] },
  {
    id: "solutions",
    label: "Solutions",
    icon: Layers,
    sheet: "solutions",
    matchPaths: ["/notation", "/demo"],
  },
  { id: "resources", label: "Resources", icon: BookOpen, sheet: "resources", matchPaths: ["/roi-calculator", "/faq", "/blogs"] },
  { id: "about", label: "About", icon: Users, sheet: "about", matchPaths: ["/about", "/founder-notes", "/careers"] },
  { id: "contact", label: "Contact", icon: Mail, to: "/contact", matchPaths: ["/contact"] },
];

const GLASS_TINT = "rgba(255, 255, 255, 0.72)";
const GLASS_STYLE = {
  backgroundColor: GLASS_TINT,
  backdropFilter: "blur(22px) saturate(160%)",
  WebkitBackdropFilter: "blur(22px) saturate(160%)",
} as const;

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSheet, setActiveSheet] = useState<SheetId | null>(null);

  const closeSheet = () => setActiveSheet(null);

  const go = (to: string) => {
    closeSheet();
    navigate(to);
  };

  const goHash = (to: string) => {
    closeSheet();
    const [path, hash] = to.split("#");
    if (location.pathname === path) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(to);
    }
  };

  const sheetRowBase =
    "flex items-center justify-between gap-3 w-full px-4 py-3.5 rounded-2xl text-left text-[#1F3D3B] hover:bg-black/[0.06] active:bg-black/[0.1] transition-colors duration-200";

  const balooStyle = (color: string) => ({
    fontFamily: "'Baloo Bhaina 2', sans-serif",
    color,
  });

  const renderSheet = () => {
    if (activeSheet === "solutions") {
      return (
        <div className="space-y-1.5">
          <button className={sheetRowBase} onClick={() => go("/notation")}>
            <span className="text-2xl font-bold leading-none" style={balooStyle("#439CCB")}>
              notation
            </span>
            <ChevronRight className="w-5 h-5 text-[#1F3D3B]/40" />
          </button>
          <div className="pl-3 space-y-1 border-l border-black/10 ml-3">
            <button className="block w-full text-left px-4 py-2.5 rounded-xl text-sm text-[#1F3D3B]/75 hover:bg-black/[0.06] transition-colors" onClick={() => goHash("/notation#hero")}>
              Home
            </button>
            <button className="block w-full text-left px-4 py-2.5 rounded-xl text-sm text-[#1F3D3B]/75 hover:bg-black/[0.06] transition-colors" onClick={() => go("/demo")}>
              Demo
            </button>
            <button className="block w-full text-left px-4 py-2.5 rounded-xl text-sm text-[#1F3D3B]/75 hover:bg-black/[0.06] transition-colors" onClick={() => goHash("/notation#pricing")}>
              Pricing
            </button>
          </div>

          <a href="https://core.fownd.care" target="_blank" rel="noopener noreferrer" onClick={closeSheet} className={sheetRowBase}>
            <span className="text-2xl font-bold leading-none" style={balooStyle("#495B25")}>core</span>
            <ArrowUpRight className="w-5 h-5 text-[#1F3D3B]/40" />
          </a>
          <div className="flex items-center justify-between gap-3 w-full px-4 py-3.5 rounded-2xl opacity-50 cursor-not-allowed">
            <span className="text-2xl font-bold leading-none text-gray-400" style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}>
              synapse <span className="text-sm font-semibold">( coming soon )</span>
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 w-full px-4 py-3.5 rounded-2xl opacity-50 cursor-not-allowed">
            <span className="text-2xl font-bold leading-none text-gray-400" style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}>
              atlas <span className="text-sm font-semibold">( coming soon )</span>
            </span>
          </div>
        </div>
      );
    }

    if (activeSheet === "resources") {
      return (
        <div className="space-y-1.5">
          <button className={sheetRowBase} onClick={() => go("/roi-calculator")}>ROI Calculator</button>
          <button className={sheetRowBase} onClick={() => go("/faq")}>FAQs</button>
          <button className={sheetRowBase} onClick={() => go("/blogs")}>Blogs</button>
        </div>
      );
    }

    if (activeSheet === "about") {
      return (
        <div className="space-y-1.5">
          <button className={sheetRowBase} onClick={() => go("/about")}>Our Team</button>
          <button className={sheetRowBase} onClick={() => go("/founder-notes")}>Founder's Note</button>
          <button className={sheetRowBase} onClick={() => go("/careers")}>Careers</button>
        </div>
      );
    }

    return null;
  };

  const sheetTitle =
    activeSheet === "solutions" ? "Solutions" : activeSheet === "resources" ? "Resources" : activeSheet === "about" ? "About Us" : "";

  const handleTab = (tab: MobileTab) => {
    if (tab.sheet) {
      setActiveSheet((prev) => (prev === tab.sheet ? null : tab.sheet!));
    } else if (tab.to) {
      go(tab.to);
    }
  };

  const isTabActive = (tab: MobileTab) => {
    // When a sheet is open, only that tab is active.
    if (activeSheet) return tab.sheet === activeSheet;
    return tab.matchPaths?.includes(location.pathname) ?? false;
  };

  const activeIndex = MOBILE_TABS.findIndex((tab) => isTabActive(tab));

  return (
    <div className="md:hidden">
      {/* Backdrop + bottom sheet */}
      <AnimatePresence>
        {activeSheet && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={closeSheet}
              className="fixed inset-0 z-[60] bg-black/25 backdrop-blur-[2px]"
            />
            <motion.div
              initial={{ x: "-50%", y: "110%", opacity: 0.6 }}
              animate={{ x: "-50%", y: 0, opacity: 1 }}
              exit={{ x: "-50%", y: "110%", opacity: 0.6 }}
              transition={{ type: "spring", stiffness: 380, damping: 36 }}
              className="fixed left-1/2 bottom-[6.25rem] z-[61] w-[calc(100%-1.5rem)] max-w-md rounded-[1.75rem] border border-white/50 p-4 shadow-[0_20px_60px_rgba(31,61,59,0.18)]"
              style={GLASS_STYLE}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-sm font-semibold text-[#1F3D3B]/55 font-productSans tracking-wide uppercase">
                  {sheetTitle}
                </span>
                <button onClick={closeSheet} aria-label="Close menu" className="w-8 h-8 rounded-full flex items-center justify-center text-[#1F3D3B]/55 hover:text-[#1F3D3B] hover:bg-black/[0.06] transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="font-productSans">{renderSheet()}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Liquid-glass tab bar */}
      <motion.div
        initial={{ x: "-50%", y: 120, opacity: 0 }}
        animate={{ x: "-50%", y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 30, delay: 0.15 }}
        className="fixed bottom-4 left-1/2 z-[62] w-[calc(100%-1.5rem)] max-w-md"
      >
        <div
          className="rounded-full border border-white/55 px-1.5 py-1.5 shadow-[0_12px_40px_rgba(31,61,59,0.16)]"
          style={GLASS_STYLE}
        >
          <div className="relative flex items-stretch">
            {/* Single sliding highlight — glides between tabs */}
            <motion.div
              aria-hidden="true"
              className="absolute top-0 bottom-0 left-0 rounded-full bg-[#1F3D3B]/10 border border-[#1F3D3B]/8 pointer-events-none"
              style={{ width: `${100 / MOBILE_TABS.length}%` }}
              initial={false}
              animate={{
                x: `${Math.max(activeIndex, 0) * 100}%`,
                opacity: activeIndex < 0 ? 0 : 1,
              }}
              transition={{ type: "spring", stiffness: 260, damping: 28, mass: 0.9 }}
            />

            {MOBILE_TABS.map((tab) => {
              const active = isTabActive(tab);
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTab(tab)}
                  aria-label={tab.label}
                  className="relative z-10 flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-full transition-colors duration-200"
                >
                  <Icon
                    className={`w-[1.35rem] h-[1.35rem] transition-colors duration-300 ${
                      active ? "text-[#1F3D3B]" : "text-[#1F3D3B]/55"
                    }`}
                    strokeWidth={active ? 2.4 : 2}
                  />
                  <span
                    className={`text-[0.62rem] font-medium font-productSans transition-colors duration-300 ${
                      active ? "text-[#1F3D3B]" : "text-[#1F3D3B]/55"
                    }`}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Navbar = ({ ...props }) => {
  let navigate = useNavigate();
  let location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      setIsResourcesOpen(false);
      setIsAboutOpen(false);
      setIsSolutionsOpen(false);
    };
    if (isResourcesOpen || isAboutOpen || isSolutionsOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isResourcesOpen, isAboutOpen, isSolutionsOpen]);

  const navLinks: NavItem[] = [
    { name: "Home", path: "/", isHash: false },
    { name: "Solutions", path: "#", isHash: false, hasDropdown: true },
    { name: "Resources", path: "#", isHash: false, hasDropdown: true },
    { name: "About Us", path: "#", isHash: false, hasDropdown: true },
    { name: "Contact Us", path: "/contact", isHash: false },
  ];

  const getNavItemClasses = (isHighlighted: boolean) =>
    `relative px-4 py-2.5 text-base font-medium font-productSans text-white transition-all duration-300 hover:opacity-90 ${
      isHighlighted ? "font-semibold drop-shadow-[0_0_12px_rgba(87,170,217,0.5)]" : ""
    }`;

  const getNavHighlightState = (
    isActive: boolean,
    isDropdownOpen: boolean,
    anyDropdownOpen: boolean
  ) => {
    const showIndicator = isDropdownOpen || (isActive && !anyDropdownOpen);
    const isHighlighted = showIndicator;
    return { showIndicator, isHighlighted };
  };

  const NavLinkItem: React.FC<NavLinkProps> = ({
    item,
    isActive,
    isDropdownOpen,
    anyDropdownOpen,
    onToggleDropdown,
    onCloseDropdown,
  }) => {
    const { showIndicator, isHighlighted } = getNavHighlightState(
      isActive,
      isDropdownOpen,
      anyDropdownOpen
    );

    if (item.isHash) {
      return (
        <HashLink to={item.path} className={getNavItemClasses(isHighlighted)}>
          {showIndicator && <ActiveIndicator />}
          <span className="relative z-10">{renderNavLabel(item.name)}</span>
        </HashLink>
      );
    }

    if (item.hasDropdown) {
      return (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onToggleDropdown}
            aria-expanded={isDropdownOpen}
            aria-label={`Toggle ${item.name} menu`}
            className={`${getNavItemClasses(isHighlighted)} cursor-pointer`}
          >
            {showIndicator && <ActiveIndicator />}
            <span className="relative z-10 flex items-center gap-1">
              <span>{renderNavLabel(item.name)}</span>
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-300 ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </span>
          </button>

          <AnimatePresence>
            {isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
                className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border-0 outline-none ${
                  item.name === "Solutions" ? "overflow-visible p-1.5" : "overflow-hidden"
                }`}
              >
                {item.name === "Solutions" ? (
                  <>
                    {/* notation — hover reveals its sub-sections */}
                    <div className="relative group/nota">
                      <Link
                        to="/notation"
                        onClick={onCloseDropdown}
                        className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-base font-semibold font-productSans hover:bg-[#1F3D3B]/35 transition-all duration-300"
                      >
                        <span
                          className="text-[1.25em] leading-none font-bold text-[#439CCB]"
                          style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}
                        >
                          notation
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </Link>

                      {/* Flyout submenu */}
                      <div className="absolute top-0 left-full w-52 bg-white rounded-xl shadow-xl border-0 outline-none overflow-hidden opacity-0 invisible translate-x-1 group-hover/nota:opacity-100 group-hover/nota:visible group-hover/nota:translate-x-0 transition-all duration-200 z-50">
                        <div
                          onClick={() => {
                            if (location.pathname === "/notation") {
                              const heroSection = document.getElementById("hero");
                              if (heroSection) {
                                heroSection.scrollIntoView({ behavior: "smooth" });
                              }
                            } else {
                              navigate("/notation#hero");
                            }
                            onCloseDropdown();
                          }}
                          className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300 cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                            </svg>
                          </div>
                          Home
                        </div>
                        <Link
                          to="/demo"
                          className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                          onClick={onCloseDropdown}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                            <Play className="w-4 h-4 text-white" />
                          </div>
                          Demo
                        </Link>
                        <HashLink
                          smooth
                          to="/notation#pricing"
                          className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                          onClick={onCloseDropdown}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                            </svg>
                          </div>
                          Pricing
                        </HashLink>
                      </div>
                    </div>

                    {/* core */}
                    <a
                      href="https://core.fownd.care"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onCloseDropdown}
                      className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-base font-semibold font-productSans hover:bg-[#1F3D3B]/35 transition-all duration-300"
                    >
                      <span
                        className="text-[1.25em] leading-none font-bold"
                        style={{ fontFamily: "'Baloo Bhaina 2', sans-serif", color: "#495B25" }}
                      >
                        core
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-gray-400" />
                    </a>

                    {/* synapse — coming soon */}
                    <div
                      aria-disabled="true"
                      className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-base font-semibold font-productSans cursor-not-allowed opacity-50"
                    >
                      <span
                        className="text-[1.25em] leading-none font-bold text-gray-400"
                        style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}
                      >
                        synapse{" "}
                        <span className="text-[0.72em] font-semibold text-gray-400">( coming soon )</span>
                      </span>
                    </div>

                    {/* atlas — coming soon */}
                    <div
                      aria-disabled="true"
                      className="flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-base font-semibold font-productSans cursor-not-allowed opacity-50"
                    >
                      <span
                        className="text-[1.25em] leading-none font-bold text-gray-400"
                        style={{ fontFamily: "'Baloo Bhaina 2', sans-serif" }}
                      >
                        atlas{" "}
                        <span className="text-[0.72em] font-semibold text-gray-400">( coming soon )</span>
                      </span>
                    </div>
                  </>
                ) : item.name === "About Us" ? (
                  <>
                    <Link
                      to="/about"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                      onClick={onCloseDropdown}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                      Our Team
                    </Link>
                    <Link
                      to="/founder-notes"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                      onClick={onCloseDropdown}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                        </svg>
                      </div>
                      Founder's Note
                    </Link>
                    <Link
                      to="/careers"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                      onClick={onCloseDropdown}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6a2 2 0 114 0v1H8V6zM6 9a1 1 0 012 0v1a1 1 0 11-2 0V9zm8 0a1 1 0 012 0v1a1 1 0 11-2 0V9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      Careers
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/roi-calculator"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                      onClick={onCloseDropdown}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                        </svg>
                      </div>
                      ROI Calculator
                    </Link>
                    <Link
                      to="/faq"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                      onClick={onCloseDropdown}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </div>
                      FAQs
                    </Link>
                    <Link
                      to="/blogs"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-productSans text-gray-600 hover:text-[#1F3D3B] hover:bg-[#1F3D3B]/35 transition-all duration-300"
                      onClick={onCloseDropdown}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#7DD3FC] to-[#166534] flex items-center justify-center shadow-lg">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6zm2 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm1 3a1 1 0 100 2h2a1 1 0 100-2H9zm-1 5a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      Blogs
                    </Link>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <Link to={item.path} className={getNavItemClasses(isHighlighted)}>
        {showIndicator && <ActiveIndicator />}
        <span className="relative z-10">{renderNavLabel(item.name)}</span>
      </Link>
    );
  };

  const ctaButtonBase =
    "inline-flex items-center justify-center gap-1.5 h-10 min-w-[10.5rem] px-5 text-sm font-medium font-productSans rounded-full transition-all duration-300";

  const isNotationPage = location.pathname === "/notation";
  const showLoginButton = location.pathname === "/" || isNotationPage;

  const loginButtonClass =
    "inline-flex h-10 items-center justify-center overflow-hidden rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]";

  const loginButtonDesktop = (
    <a
      href="https://notation.fownd.care/login"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Login"
      className={`group relative w-10 shrink-0 transition-[width,background-color,border-color] duration-300 ease-out hover:w-[7.25rem] hover:bg-white/20 hover:border-white/40 ${loginButtonClass}`}
    >
      <LogIn
        className="absolute h-4 w-4 transition-all duration-300 ease-out group-hover:scale-75 group-hover:opacity-0"
        strokeWidth={2.25}
      />
      <span className="absolute inset-0 flex items-center justify-center whitespace-nowrap text-sm font-medium font-productSans opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100">
        Login
      </span>
    </a>
  );

  const loginButtonMobile = (
    <a
      href="https://notation.fownd.care/login"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Login"
      className="inline-flex h-10 items-center justify-center rounded-full border border-white/25 bg-white/10 px-5 text-sm font-medium font-productSans text-white backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] transition-colors duration-300 hover:bg-white/20 hover:border-white/40"
    >
      Login
    </a>
  );

  return (
    <>
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`absolute md:fixed top-0 left-0 right-0 z-50 pointer-events-none md:pointer-events-auto transition-all duration-300 ${
        isScrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`transition-all duration-500 border border-transparent ${
            isScrolled
              ? "md:rounded-full md:border-white/10 md:px-5 md:py-2 md:shadow-[0_8px_32px_rgba(0,0,0,0.22)] md:bg-[rgba(18,38,36,0.55)] md:[backdrop-filter:blur(18px)_saturate(140%)] md:[-webkit-backdrop-filter:blur(18px)_saturate(140%)]"
              : ""
          }`}
        >
        <div className="hidden md:grid md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-4">
          <div
            onClick={() => navigate(`/`)}
            className="flex items-center justify-start cursor-pointer"
          >
            <img
              src="/Fownd_A21.png"
              alt="Fownd Logo"
              className={`transition-all duration-500 ${
                isScrolled ? "w-10 h-10 md:w-11 md:h-11" : "w-12 h-12 md:w-14 md:h-14"
              }`}
            />
          </div>

          <div className="flex items-center justify-center gap-1">
            {navLinks.map((item, index) => {
              const isActive = isNavItemActive(item, location.pathname);
              const anyDropdownOpen = isAboutOpen || isResourcesOpen || isSolutionsOpen;
              const isDropdownOpen =
                item.name === "About Us"
                  ? isAboutOpen
                  : item.name === "Solutions"
                    ? isSolutionsOpen
                    : item.name === "Resources"
                      ? isResourcesOpen
                      : false;

              const handleToggleDropdown = () => {
                if (item.name === "About Us") {
                  setIsResourcesOpen(false);
                  setIsSolutionsOpen(false);
                  setIsAboutOpen(!isAboutOpen);
                } else if (item.name === "Solutions") {
                  setIsAboutOpen(false);
                  setIsResourcesOpen(false);
                  setIsSolutionsOpen(!isSolutionsOpen);
                } else if (item.name === "Resources") {
                  setIsAboutOpen(false);
                  setIsSolutionsOpen(false);
                  setIsResourcesOpen(!isResourcesOpen);
                }
              };

              return (
                <NavLinkItem
                  key={index}
                  item={item}
                  isActive={isActive}
                  isDropdownOpen={isDropdownOpen}
                  anyDropdownOpen={anyDropdownOpen}
                  onToggleDropdown={handleToggleDropdown}
                  onCloseDropdown={() => {
                    setIsAboutOpen(false);
                    setIsSolutionsOpen(false);
                    setIsResourcesOpen(false);
                  }}
                />
              );
            })}
          </div>

          <div className="flex items-center justify-end gap-2.5">
            {showLoginButton && loginButtonDesktop}
            <Link
              to="https://notation.fownd.care/"
              target="_blank"
              rel="noopener noreferrer"
              className={`${ctaButtonBase} group relative overflow-hidden text-white bg-gradient-to-r from-[#58ACDD] to-[#486633] hover:from-[#4A9BC9] hover:to-[#3A5A1F] shadow-lg hover:shadow-[0_20px_40px_rgba(74,155,201,0.4)] hover:scale-110`}
            >
              {/* Animated background pulse */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#58ACDD] to-[#486633] opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>

              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"></div>

              <span className="relative z-10 group-hover:text-white transition-colors duration-300">
                <NotationButtonLabel prefix="Try" />
              </span>

              {/* Glow effect */}
              <div className="absolute inset-0 bg-white/10 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Border animation */}
              <div className="absolute inset-0 rounded-full border-2 border-white/0 group-hover:border-white/30 transition-all duration-500"></div>
            </Link>
          </div>
        </div>

        <div className="relative flex md:hidden items-center justify-center min-h-10">
          {showLoginButton && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10 pointer-events-auto">
              {loginButtonMobile}
            </div>
          )}
          <div
            onClick={() => navigate(`/`)}
            className="flex items-center cursor-pointer pointer-events-auto"
          >
            <img
              src="/Fownd_A21.png"
              alt="Fownd Logo"
              className={`transition-all duration-500 ${
                isScrolled ? "w-10 h-10" : "w-12 h-12"
              }`}
            />
          </div>
        </div>
        </div>
      </div>
    </motion.nav>

    <MobileBottomNav />
    </>
  );
};

export default Navbar;
